import React, { useState, useEffect, useCallback } from 'react';
import { Heart, ArrowRight, ArrowLeft, CheckCircle, Save, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert, ProgressBar } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Task-specific configuration moved from taskTypes.js
export const TaskGriefJournalingConfig = {
  'task_003': {
    sequence: 1,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'What did you miss today about your loved one?',
        type: 'text',
        required: true,
        placeholder: 'What specific things about them did you miss today?'
      },
      {
        id: 'q2',
        text: 'When was the hardest time of today?',
        type: 'text',
        required: true,
        placeholder: 'Describe the most difficult moment you experienced today...'
      },
      {
        id: 'q3',
        text: 'What have you been feeling most of lately? Where did you feel it in your body?',
        type: 'text',
        required: true,
        placeholder: 'Describe your emotions and where you feel them physically...'
      }
    ]
  },
  'task_004': {
    sequence: 2,
    prerequisites: ['task_003'],
    questions: [
      {
        id: 'q1',
        text: 'What do you tend to feel when you\'re hit by a wave of grief?',
        type: 'text',
        required: true,
        placeholder: 'Describe the physical and emotional sensations...'
      },
      {
        id: 'q2',
        text: 'What tends to trigger your feelings of grief (events, holidays, songs, places, certain people, etc.)?',
        type: 'text',
        required: true,
        placeholder: 'List the things that often bring up your grief...'
      }
    ]
  },
  'task_005': {
    sequence: 3,
    prerequisites: ['task_003', 'task_004'],
    questions: [
      {
        id: 'q1',
        text: 'What\'s one comforting memory of your loved one you remember?',
        type: 'text',
        required: true,
        placeholder: 'Share a memory that brings you comfort...'
      },
      {
        id: 'q2',
        text: 'What\'s one thing your loved one did or said that meant/means so much to you?',
        type: 'text',
        required: true,
        placeholder: 'Describe something special they did or said...'
      },
      {
        id: 'q3',
        text: 'When do you feel most connected to your loved one?',
        type: 'text',
        required: true,
        placeholder: 'Describe moments when you feel closest to them...'
      }
    ]
  },
  'task_006': {
    sequence: 4,
    prerequisites: ['task_003', 'task_004', 'task_005'],
    questions: [
      {
        id: 'q1',
        text: 'How can you honor your loved one? Is there something you can do or give to celebrate their memory?',
        type: 'text',
        required: true,
        placeholder: 'Think about ways to honor and celebrate their life...'
      },
      {
        id: 'q2',
        text: 'Is there something that you need to forgive your loved one for?',
        type: 'text',
        required: false,
        placeholder: 'If there are things to forgive, write about them...'
      },
      {
        id: 'q3',
        text: 'Is there something that you need to forgive yourself for?',
        type: 'text',
        required: false,
        placeholder: 'If there are things to forgive yourself for, write about them...'
      }
    ]
  },
  'task_007': {
    sequence: 5,
    prerequisites: ['task_003', 'task_004', 'task_005', 'task_006'],
    questions: [
      {
        id: 'q1',
        text: 'When you felt overwhelmed with grief in the past, what helped you cope?',
        type: 'text',
        required: true,
        placeholder: 'What strategies have worked for you before?'
      },
      {
        id: 'q2',
        text: 'What can you do to take care of yourself when you feel overwhelmed by a difficult emotion?',
        type: 'text',
        required: true,
        placeholder: 'Describe your self-care strategies...'
      },
      {
        id: 'q3',
        text: 'What do you need to do more of or less of to show yourself compassion?',
        type: 'text',
        required: true,
        placeholder: 'Reflect on how you can be kinder to yourself...'
      },
      {
        id: 'q4',
        text: 'What do you need to remind yourself of to cope with difficult times? Write a mantra you can use when you feel overwhelmed by grief.',
        type: 'text',
        required: true,
        placeholder: 'Create a personal mantra or reminder...'
      }
    ]
  },
  'task_008': {
    sequence: 6,
    prerequisites: ['task_003', 'task_004', 'task_005', 'task_006', 'task_007'],
    questions: [
      {
        id: 'q1',
        text: 'Who can you turn to for support when you\'re feeling overwhelmed? Do you feel comfortable asking for help? Why or why not?',
        type: 'text',
        required: true,
        placeholder: 'Think about your support network and comfort with asking for help...'
      },
      {
        id: 'q2',
        text: 'What is something you wish your support system would understand?',
        type: 'text',
        required: true,
        placeholder: 'What would you like others to know about your grief?'
      }
    ]
  },
  'task_009': {
    sequence: 7,
    prerequisites: ['task_003', 'task_004', 'task_005', 'task_006', 'task_007', 'task_008'],
    questions: [
      {
        id: 'q1',
        text: 'Write a letter to your loved one.',
        type: 'text',
        required: true,
        placeholder: 'Write whatever you want to say to them...'
      },
      {
        id: 'q2',
        text: 'Write a letter from your loved one to yourself.',
        type: 'text',
        required: true,
        placeholder: 'What do you think they would say to you?'
      }
    ]
  },
  'task_010': {
    sequence: 8,
    prerequisites: ['task_003', 'task_004', 'task_005', 'task_006', 'task_007', 'task_008', 'task_009'],
    questions: [
      {
        id: 'q1',
        text: 'In the absence of your loved one, what\'s your biggest motivation?',
        type: 'text',
        required: true,
        placeholder: 'What drives you forward now?'
      },
      {
        id: 'q2',
        text: 'What\'s something you are still learning to accept?',
        type: 'text',
        required: true,
        placeholder: 'What aspects of your loss are you working to accept?'
      }
    ]
  }
};

const TaskGriefJournaling = ({ task, taskType, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);

  // Get questions from task-specific configuration
  const questions = TaskGriefJournalingConfig[task.taskTypeId]?.questions || [];

  // Auto-save functionality
  const saveProgress = useCallback(async (questionId, answer, questionIndex) => {
    if (!session?.sessionId) return;

    setIsSaving(true);
    try {
      await apiPost(
        '/.netlify/functions/journalingTaskApi',
        {
          sessionId: session.sessionId,
          questionId: questionId,
          answer: answer,
          currentQuestionIndex: questionIndex
        },
        user,
        userInfo?.userId
      );

      setLastSaved(new Date());
      
      // Progress saved successfully - no auto-completion logic
      // Don't show toast for auto-save to avoid spam
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, task?.taskId, user, userInfo?.userId]);

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (questionId, answer, questionIndex) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(questionId, answer, questionIndex);
        }, 10000); // 10 seconds
      };
    })(),
    [saveProgress]
  );

  // Load progress from session (following onboarding pattern)
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing answers if session has data
    if (session.sessionData?.answers) {
      setAnswers(session.sessionData.answers);
      setCurrentQuestionIndex(session.sessionData.currentQuestionIndex || 0);
    }
  }, [session, hasLoadedProgress]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Auto-save with debouncing
    debouncedSave(questionId, value, currentQuestionIndex);
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Save current answer immediately before moving to next question
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && answers[currentQuestion.id]) {
        try {
          await saveProgress(currentQuestion.id, answers[currentQuestion.id], currentQuestionIndex);
        } catch (error) {
          console.error('Error saving progress on next:', error);
          // Don't block navigation if save fails
        }
      }
      
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = async () => {
    if (currentQuestionIndex > 0) {
      // Save current answer immediately before moving to previous question
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && answers[currentQuestion.id]) {
        try {
          await saveProgress(currentQuestion.id, answers[currentQuestion.id], currentQuestionIndex);
        } catch (error) {
          console.error('Error saving progress on previous:', error);
          // Don't block navigation if save fails
        }
      }
      
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleManualSave = async () => {
    if (!currentQuestion) return;

    const currentAnswer = answers[currentQuestion.id] || '';
    await saveProgress(currentQuestion.id, currentAnswer, currentQuestionIndex);
    toast.success('Progress saved!');
  };

  const handleComplete = async () => {
    if (!currentQuestion) return;

    setIsSubmitting(true);
    try {
      // First, save completion data to the session
      const completionData = {
        answers: answers,
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/journalingTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        user,
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        user,
        userInfo?.userId
      );

      toast.success('Journaling completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading journaling questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
            <Heart className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{taskType.pageTitle}</h1>
            <p className="text-gray-600">{taskType.pageDescription}</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <ProgressBar
          current={currentQuestionIndex + 1}
          total={questions.length}
          variant="red"
          size="medium"
          showPercentage={true}
          label={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
        />
      </div>

      {/* Current Question */}
      <Card variant="elevated" padding="large" className="mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {currentQuestion.text}
              {currentQuestion.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              type="textarea"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={6}
              className="resize-none"
            />
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          variant="ghost"
          size="medium"
          className="flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex items-center space-x-4">
          <Button
            onClick={handleManualSave}
            disabled={isSaving}
            variant="ghost"
            size="medium"
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              variant="error"
              size="medium"
              className="flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Completing...' : 'Complete Journaling'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="error"
              size="medium"
              className="flex items-center"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Save Status */}
      {lastSaved && (
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
          <Clock className="h-4 w-4 mr-2" />
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Helpful Tips */}
      <Alert variant="info" className="mt-8">
        <h3 className="text-sm font-medium text-red-800 mb-2">💡 Journaling Tips</h3>
        <ul className="text-sm text-red-700 space-y-1">
          <li>• There are no right or wrong answers - write from your heart</li>
          <li>• Take breaks if you feel overwhelmed - you can always return later</li>
          <li>• Your progress is automatically saved every 10 seconds</li>
          <li>• You can navigate between questions freely</li>
          <li>• This activity is designed to help you process your grief in a safe way</li>
        </ul>
      </Alert>
    </div>
  );
};

// Add static methods to the component for Summary and ResultsSummary
TaskGriefJournaling.Summary = ({ task, session }) => {
  const answeredCount = session?.answers ? Object.keys(session.answers).length : 0;
  const totalQuestions = TaskGriefJournalingConfig[task.taskTypeId]?.questions?.length || 0;
  
  return (
    <div className="grief-journaling-summary">
      <div className="flex items-center space-x-2">
        <Heart className="h-4 w-4 text-pink-600" />
        <span className="text-xs text-gray-500">
          {answeredCount}/{totalQuestions} questions answered
        </span>
      </div>
    </div>
  );
};

TaskGriefJournaling.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData?.answers) return null;

  const questions = TaskGriefJournalingConfig[task.taskTypeId]?.questions || [];
  
  return (
    <div className="grief-journaling-results-summary">
      <h3 className="font-medium text-gray-900 mb-2">Grief Journaling Results - {patientName}</h3>
      <div className="space-y-3 text-sm">
        {questions.map(question => (
          <div key={question.id} className="border-l-2 border-pink-200 pl-3">
            <p className="font-medium text-gray-700 mb-1">{question.text}</p>
            <p className="text-gray-600">{completionData.answers[question.id] || 'No answer provided'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Add static method for task assignment form
TaskGriefJournaling.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  // No custom form needed - return null
  return null;
};

export default TaskGriefJournaling;